// DiscoverFlixMoreRow.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { fromEvent, animationFrameScheduler, from } from 'rxjs';
import {
  debounceTime,
  pairwise,
  map,
  switchMap,
  reduce,
  filter,
  startWith,
} from 'rxjs/operators';

import FlixListItem from '../container/FlixListItem.js';
import FlixCard from '../container/FlixCard.js';
import { InfiniteScroller, ScrollItem } from './InfiniteScroller.jsx';
import WithIntersectionObserver from './WithIntersectionObserver.jsx';

import getIsInAutoPlayArea from '../resource/getIsInAutoPlayArea.js';
import getIsMatchedByMediaQuery, {
  Key,
} from '../resource/getIsMatchedByMediaQuery.js';

import media from '../style/media.js';

export const DiscoverFlixMoreRow = ({
  category = '',
  nextPage = null,
  feedUnixTimestamp = null,
  shouldShowListLayoutOnMobile = false,
  isFetching = false,
  shouldRefresh = false,
  messageIds = [],
  fetchFeeds = () => null,
}) => {
  const messageRef = useRef({});
  const subscription = useRef(null);
  const [autoplayMessageId, setAutoplayMessageId] = useState(null);
  const isOnDesktop = getIsMatchedByMediaQuery({ key: Key.DESKTOP });
  useEffect(() => {
    fetchFeeds({ page: 1, type: category, unixTimestamp: feedUnixTimestamp });
  }, [category, feedUnixTimestamp, fetchFeeds]);
  useEffect(() => {
    if (shouldRefresh) {
      fetchFeeds({
        page: 1,
        type: category,
        shouldSkipLocalCache: shouldRefresh,
        unixTimestamp: feedUnixTimestamp,
      });
    }
  }, [category, feedUnixTimestamp, fetchFeeds, shouldRefresh]);
  useEffect(() => {
    if (!isOnDesktop) {
      subscription.current = fromEvent(window, 'scroll')
        .pipe(
          startWith(null),
          debounceTime(200, animationFrameScheduler),
          map(() => window.pageYOffset),
          pairwise(),
          map(pair => pair[1] - pair[0]),
          filter(deltaY => Math.abs(deltaY) >= 10),
          switchMap(deltaY =>
            from(Object.values(messageRef.current)).pipe(
              reduce((acc, data, index) => {
                if (!data) return acc;
                return getIsInAutoPlayArea({ ref: data.ref })
                  ? acc.concat(index)
                  : acc;
              }, []),
              map(inCenterIndex => ({ inCenterIndex, deltaY }))
            )
          ),
          map(({ deltaY, inCenterIndex }) => {
            if (inCenterIndex.length <= 0) return null;
            const shouldPlayIndex =
              deltaY >= 0
                ? Math.max(...inCenterIndex)
                : Math.min(...inCenterIndex);
            return shouldPlayIndex
              ? Object.values(messageRef.current)[shouldPlayIndex]?.id
              : null;
          })
        )
        .subscribe(messageId => setAutoplayMessageId(messageId));
    }
    return () => {
      subscription.current?.unsubscribe();
    };
  });
  const PlaceholderComponent = shouldShowListLayoutOnMobile ? (
    <div>
      <ListItemWrapper isShowOnMobileOnly>
        <FlixListItem messageId={null} />
      </ListItemWrapper>
      <GridItemWrapper isShowAboveMobile>
        <FlixCard messageId={null} shouldShowAvatar shouldShowUsername />
      </GridItemWrapper>
    </div>
  ) : (
    <GridItemWrapper>
      <FlixCard messageId={null} shouldShowAvatar shouldShowUsername />
    </GridItemWrapper>
  );
  const renderMessage = (messageId, index) => {
    if (shouldShowListLayoutOnMobile) {
      return (
        <WithIntersectionObserver key={messageId}>
          {({ isIntersecting }) => (
            <div
              ref={el => {
                if (el) {
                  messageRef.current[messageId] = {
                    ref: el,
                    id: messageId,
                  };
                }
              }}
            >
              <ListItemWrapper isShowOnMobileOnly>
                <FlixListItem
                  shouldUseLink
                  shouldUseLazyImage={false}
                  messageId={messageId}
                  id={messageId}
                  categoryId={category}
                  itemIndexInCategory={index}
                />
              </ListItemWrapper>
              <GridItemWrapper isShowAboveMobile>
                <FlixCard
                  messageId={isIntersecting ? messageId : null}
                  shouldPlay={isIntersecting && messageId === autoplayMessageId}
                  categoryId={category}
                  categoryIndex={index}
                  itemIndexInCategory={index}
                  shouldTrackEvent
                  shouldShowTooltip
                  shouldShowAvatar
                  shouldShowUsername
                  shouldAnimateLoader={isIntersecting}
                  isIntersecting={isIntersecting}
                />
              </GridItemWrapper>
            </div>
          )}
        </WithIntersectionObserver>
      );
    }
    return (
      <WithIntersectionObserver key={messageId}>
        {({ isIntersecting }) => (
          <GridItemWrapper
            ref={el => {
              if (el) {
                messageRef.current[messageId] = {
                  ref: el,
                  id: messageId,
                };
              }
            }}
          >
            <FlixCard
              messageId={isIntersecting ? messageId : null}
              shouldPlay={isIntersecting && messageId === autoplayMessageId}
              categoryId={category}
              categoryIndex={index}
              itemIndexInCategory={index}
              shouldTrackEvent
              shouldShowTooltip
              shouldShowAvatar
              shouldShowUsername
              shouldAnimateLoader={isIntersecting}
              isIntersecting={isIntersecting}
            />
          </GridItemWrapper>
        )}
      </WithIntersectionObserver>
    );
  };
  return (
    <StyledDiscoverFlixMoreRow>
      <InfiniteScroller
        isFetching={isFetching}
        hasMore={nextPage !== null}
        useWindow={true}
        threshold={100}
        loadMore={() =>
          fetchFeeds({
            page: nextPage,
            type: category,
            unixTimestamp: feedUnixTimestamp,
          })
        }
      >
        <Body shouldShowListLayoutOnMobile={shouldShowListLayoutOnMobile}>
          <ScrollItem loader={PlaceholderComponent}>
            {messageIds.map(renderMessage)}
          </ScrollItem>
        </Body>
      </InfiniteScroller>
    </StyledDiscoverFlixMoreRow>
  );
};

DiscoverFlixMoreRow.propTypes = {
  category: PropTypes.string,
  fetchFeeds: PropTypes.func,
  shouldShowListLayoutOnMobile: PropTypes.bool,
  isFetching: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  messageIds: PropTypes.array,
  nextPage: PropTypes.number,
  feedUnixTimestamp: PropTypes.number,
};

const StyledDiscoverFlixMoreRow = styled.div``;

const Body = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(224px, 1fr));
  gap: 8px;
  padding-left: 16px;
  padding-right: 16px;
  ${({ shouldShowListLayoutOnMobile }) => {
    if (shouldShowListLayoutOnMobile) {
      return css`
        ${media.mobile`
          padding-left: 0px;
          padding-right: 0px;
          display: block;
          gap: normal
          grid-template-columns: none;
        `};
      `;
    }
  }}
`;

const GridItemWrapper = styled.div`
  border-radius: 4px;
  :last-child {
    margin-right: 0;
  }
  height: 100%;
  ${({ isShowAboveMobile }) => {
    if (isShowAboveMobile) {
      return css`
        display: block;
        ${media.mobile`
          display: none;
        `};
      `;
    }
  }}
`;

const ListItemWrapper = styled.div`
  ${media.mobile`
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(100, 100, 100, 0.15);
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;
    :last-child {
      margin-bottom: 0px;
      border-bottom: none;
    }
  `};
  ${({ isShowOnMobileOnly }) => {
    if (isShowOnMobileOnly) {
      return css`
        display: none;
        ${media.mobile`
          display: block;
        `};
      `;
    }
  }}
`;

export default DiscoverFlixMoreRow;
